import {
  buildDocumentS3Key,
  detectDocumentCategory,
  formatAccessPermission,
  logAnalyticUpload,
  mapAnalyticUploadLogStatus,
} from '../../services/documents/analyticUploadLogService';
import { documentService } from '../../services/documents/documentService';
import {
  documentDebugError,
  documentDebugLog,
  documentDebugWarn,
} from '../../services/documents/documentDebug';
import {
  DocumentStatusResponse,
  DocumentUploadResult,
} from '../../services/documents/documentTypes';
import {
  isDocumentCompleted,
  isDocumentFailed,
  normalizeAllowedRoles,
  normalizeDocumentStatus,
} from '../../services/documents/documentStatusUtils';
import {
  DocumentProcessingStatus,
  DocumentsActionTypes,
  DocumentsStore,
  UploadedDocument,
} from './documentsTypes';
import { RootStore } from '../index';
import { UserRoles } from '../../shared/constants';
import { toast } from 'react-toastify';
import { AnyAction, Dispatch } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

interface DocumentUploadPayload {
  file: File;
  permissions: UserRoles[];
}

const LOG_PREFIX = '[DocumentUpload]';

type DocumentsDispatch = ThunkDispatch<DocumentsStore, void, AnyAction> &
  Dispatch;

const normalizeFilename = (filename: string): string =>
  filename.trim().toLowerCase().replace(/_+/g, '_').replace(/\s+/g, ' ');

const findUploadedDocumentByFilename = (
  documents: UploadedDocument[],
  filename: string,
): UploadedDocument | undefined =>
  documents.find(
    (document) =>
      normalizeFilename(document.filename) === normalizeFilename(filename),
  );

const isDuplicateDocumentError = (message: string): boolean =>
  message.toLowerCase().includes('already exists');

export const setUploadedDocuments = (documents: UploadedDocument[]) => ({
  type: DocumentsActionTypes.SET_UPLOADED_DOCUMENTS,
  payload: documents,
});

export const addUploadedDocuments = (documents: UploadedDocument[]) => ({
  type: DocumentsActionTypes.ADD_UPLOADED_DOCUMENTS,
  payload: documents,
});

export const updateUploadedDocumentStatus = (
  documentId: string,
  status: DocumentProcessingStatus,
) => ({
  type: DocumentsActionTypes.UPDATE_UPLOADED_DOCUMENT_STATUS,
  payload: { documentId, status: normalizeDocumentStatus(status) },
});

export const removeUploadedDocument = (documentId: string) => ({
  type: DocumentsActionTypes.REMOVE_UPLOADED_DOCUMENT,
  payload: documentId,
});

export const setDocumentsUploading = (isUploading: boolean) => ({
  type: DocumentsActionTypes.SET_DOCUMENTS_UPLOADING,
  payload: isUploading,
});

export const setDocumentsLoading = (isLoading: boolean) => ({
  type: DocumentsActionTypes.SET_DOCUMENTS_LOADING,
  payload: isLoading,
});

const resolveAllowedRolesFromStatus = (
  status: DocumentStatusResponse,
  filename: string,
  knownAllowedRoles?: string[],
): string[] | undefined => {
  const candidates = [
    filename,
    status.filename,
    status.source_filename,
    status.document_id,
  ];

  for (const candidate of candidates) {
    const roles = normalizeAllowedRoles(status.allowed_roles, candidate);
    if (roles?.length) {
      return roles;
    }
  }

  return knownAllowedRoles;
};

export const mapStatusResponseToUploadedDocument = (
  status: DocumentStatusResponse,
  customerId: number,
  knownFilename?: string,
  knownAllowedRoles?: string[],
): UploadedDocument => {
  const filename = resolveDocumentFilename(status, knownFilename);

  return {
    documentId: status.document_id,
    filename,
    customerId,
    status: normalizeDocumentStatus(status.status),
    allowedRoles: resolveAllowedRolesFromStatus(
      status,
      filename,
      knownAllowedRoles,
    ),
    createdAt: status.created_at,
    updatedAt: status.updated_at,
  };
};

const resolveDocumentFilename = (
  status: DocumentStatusResponse,
  knownFilename?: string,
): string => {
  const fromApi = status.source_filename?.trim() || status.filename?.trim();
  if (fromApi) {
    return fromApi;
  }

  const preserved = knownFilename?.trim();
  if (preserved && preserved !== status.document_id) {
    return preserved;
  }

  return preserved || status.document_id;
};

const sortUploadedDocuments = (
  documents: UploadedDocument[],
): UploadedDocument[] =>
  [...documents].sort((left, right) => {
    const leftTime = Date.parse(left.updatedAt || left.createdAt || '') || 0;
    const rightTime = Date.parse(right.updatedAt || right.createdAt || '') || 0;
    return rightTime - leftTime;
  });

const toUploadedDocuments = (
  results: DocumentUploadResult[],
  customerId: number,
): UploadedDocument[] =>
  results.map((result) => {
    const statusLike: DocumentStatusResponse = {
      document_id: result.document_id,
      status: normalizeDocumentStatus(result.status || 'loaded'),
      filename: result.filename,
      allowed_roles: result.allowed_roles,
    };

    return mapStatusResponseToUploadedDocument(
      statusLike,
      customerId,
      result.filename,
    );
  });

const hasUsableAllowedRoles = (value: unknown): boolean => {
  if (value == null) {
    return false;
  }

  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  if (Array.isArray(value)) {
    return value.some(
      (role) => typeof role === 'string' && role.trim().length > 0,
    );
  }

  if (typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>).length > 0;
  }

  return false;
};

const loggedCreateDocumentIds = new Set<string>();

export const logAnalyticUploadCreateOnTerminalStatus = async (
  document: UploadedDocument,
  token: string,
): Promise<void> => {
  if (
    !isDocumentCompleted(document.status) &&
    !isDocumentFailed(document.status)
  ) {
    return;
  }

  if (loggedCreateDocumentIds.has(document.documentId)) {
    return;
  }

  // Mark before the POST so a concurrent poll/upload path can't double-log
  // the same document; rolled back below on transient failure so the entry
  // is not permanently lost.
  loggedCreateDocumentIds.add(document.documentId);

  const result = await logAnalyticUpload(document.customerId, token, {
    action: 'create',
    file_name: document.filename,
    file_s3_key: buildDocumentS3Key(document.documentId, document.filename),
    access_permission: formatAccessPermission(
      document.allowedRoles,
      document.filename,
    ),
    category: detectDocumentCategory(document.filename),
    status: mapAnalyticUploadLogStatus(document.status),
  });

  if (result === 'unavailable') {
    loggedCreateDocumentIds.delete(document.documentId);
  }
};

const mergeUploadResultWithStatus = (
  uploadResult: DocumentUploadResult,
  status: DocumentStatusResponse,
): DocumentUploadResult => ({
  ...uploadResult,
  filename:
    status.source_filename?.trim() ||
    status.filename?.trim() ||
    uploadResult.filename,
  allowed_roles: hasUsableAllowedRoles(status.allowed_roles)
    ? status.allowed_roles
    : uploadResult.allowed_roles,
  status: status.status || uploadResult.status,
});

const enrichUploadResultsFromStatusApi = async (
  results: DocumentUploadResult[],
  token: string,
  customerId: number,
): Promise<DocumentUploadResult[]> =>
  Promise.all(
    results.map(async (result) => {
      try {
        const status = await documentService.getDocumentStatus(
          result.document_id,
          token,
          customerId,
        );
        return mergeUploadResultWithStatus(result, status);
      } catch (error) {
        documentDebugWarn(LOG_PREFIX, 'Could not fetch status for new upload', {
          documentId: result.document_id,
          error,
        });
        return result;
      }
    }),
  );

export const fetchAllDocuments =
  (token: string, customerId: number, silent = false) =>
  async (dispatch: DocumentsDispatch) => {
    if (!silent) {
      dispatch(setDocumentsLoading(true));
    }

    try {
      const statuses = await documentService.getAllDocumentStatuses(
        token,
        customerId,
      );
      const existingDocumentsById = new Map<string, UploadedDocument>(
        RootStore.getState().documents.uploadedDocuments.map(
          (document: UploadedDocument) =>
            [document.documentId, document] as const,
        ),
      );

      dispatch(
        setUploadedDocuments(
          sortUploadedDocuments(
            statuses.map((status) => {
              const existingDocument = existingDocumentsById.get(
                status.document_id,
              );

              return mapStatusResponseToUploadedDocument(
                status,
                customerId,
                existingDocument?.filename,
                existingDocument?.allowedRoles,
              );
            }),
          ),
        ),
      );
    } catch (error) {
      documentDebugError(LOG_PREFIX, 'Failed to fetch document history', error);
      if (!silent) {
        const message =
          error instanceof Error
            ? error.message
            : 'Failed to load uploaded documents.';
        toast.error(message);
      }
      throw error;
    } finally {
      if (!silent) {
        dispatch(setDocumentsLoading(false));
      }
    }
  };

export type UploadDocumentsOutcome = {
  results: DocumentUploadResult[];
  uploadedFilenames: string[];
  skippedDuplicateFilenames: string[];
  failedFilenames: string[];
};

export const uploadAndTrackDocuments =
  (documents: DocumentUploadPayload[], token: string, customerId: number) =>
  async (dispatch: DocumentsDispatch): Promise<UploadDocumentsOutcome> => {
    documentDebugLog(LOG_PREFIX, 'Upload started', {
      customerId,
      fileCount: documents.length,
      filenames: documents.map((document) => document.file.name),
    });

    dispatch(setDocumentsUploading(true));

    const skippedDuplicateFilenames: string[] = [];

    try {
      const uploadedDocuments =
        RootStore.getState().documents.uploadedDocuments;
      const duplicateUploads = documents.filter((document) =>
        findUploadedDocumentByFilename(uploadedDocuments, document.file.name),
      );
      const uniqueUploads = documents.filter(
        (document) =>
          !findUploadedDocumentByFilename(
            uploadedDocuments,
            document.file.name,
          ),
      );

      skippedDuplicateFilenames.push(
        ...duplicateUploads.map((document) => document.file.name),
      );

      if (uniqueUploads.length === 0) {
        const duplicateNames = skippedDuplicateFilenames.join(', ');
        throw new Error(
          `Document "${duplicateNames}" already exists in Uploaded documents. Delete it from Uploaded documents before uploading again.`,
        );
      }

      if (skippedDuplicateFilenames.length > 0) {
        toast.warn(
          `Skipped already-uploaded file(s): ${skippedDuplicateFilenames.join(
            ', ',
          )}. Other files will still upload.`,
        );
      }

      const { results, failures } = await documentService.uploadDocuments(
        uniqueUploads,
        token,
        customerId,
      );

      documentDebugLog(LOG_PREFIX, 'Upload batch finished', {
        successCount: results.length,
        failureCount: failures.length,
        results,
        failures,
      });

      if (results.length > 0) {
        const resultsWithStatus = await enrichUploadResultsFromStatusApi(
          results,
          token,
          customerId,
        );

        dispatch(
          addUploadedDocuments(
            toUploadedDocuments(resultsWithStatus, customerId),
          ),
        );

        const terminalUploads = toUploadedDocuments(
          resultsWithStatus,
          customerId,
        ).filter(
          (document) =>
            isDocumentCompleted(document.status) ||
            isDocumentFailed(document.status),
        );
        await Promise.all(
          terminalUploads.map((document) =>
            logAnalyticUploadCreateOnTerminalStatus(document, token),
          ),
        );
      }

      try {
        await dispatch(fetchAllDocuments(token, customerId, true));
      } catch (refreshError) {
        documentDebugWarn(
          LOG_PREFIX,
          'Failed to refresh documents after upload batch',
          {
            refreshError,
          },
        );
      }

      const uploadedFilenames = results.map((result) => result.filename);
      const failedFilenames = failures.map((failure) => failure.filename);

      if (results.length > 0) {
        toast.success(
          results.length === 1
            ? 'Document uploaded. Processing has started.'
            : `${results.length} documents uploaded. Processing has started.`,
        );
      }

      if (failures.length > 0) {
        const failureSummary = failures
          .map((failure) => failure.error)
          .join(' ');
        toast.error(
          failures.length === 1
            ? failureSummary
            : `${failures.length} file(s) failed to upload. ${failureSummary}`,
        );
      }

      return {
        results,
        uploadedFilenames,
        skippedDuplicateFilenames,
        failedFilenames,
      };
    } catch (error) {
      documentDebugError(LOG_PREFIX, 'Upload failed', error);
      let message =
        error instanceof Error ? error.message : 'Failed to upload documents.';

      try {
        await dispatch(fetchAllDocuments(token, customerId, true));
      } catch (refreshError) {
        documentDebugWarn(
          LOG_PREFIX,
          'Failed to refresh documents after upload error',
          {
            refreshError,
          },
        );
      }

      if (isDuplicateDocumentError(message)) {
        const refreshedDocuments =
          RootStore.getState().documents.uploadedDocuments;
        const hasVisibleDuplicate = documents.some((document) =>
          findUploadedDocumentByFilename(
            refreshedDocuments,
            document.file.name,
          ),
        );

        if (!hasVisibleDuplicate) {
          message = `${message} The server may still be finishing deletion — wait a few seconds or rename the file.`;
        }
      }

      toast.error(message);
      throw error;
    } finally {
      dispatch(setDocumentsUploading(false));
    }
  };

export const notifyDocumentFailed = (filename: string): void => {
  toast.error(
    `${filename} processing failed. Please delete it manually to remove it.`,
  );
};

export const notifyDocumentCompleted = (filename: string): void => {
  toast.success(`${filename} processed successfully.`);
};

export const deleteDocument =
  (documentId: string, token: string, customerId: number) =>
  async (dispatch: DocumentsDispatch) => {
    const existingDocument =
      RootStore.getState().documents.uploadedDocuments.find(
        (document: UploadedDocument) => document.documentId === documentId,
      );
    const deletedFilename = existingDocument?.filename;

    documentDebugLog(LOG_PREFIX, 'Deleting document', {
      documentId,
      customerId,
    });

    const DELETE_SETTLE_MS = 10_000;
    let progressToastId: ReturnType<typeof toast.info> | undefined;

    try {
      await documentService.deleteDocument(documentId, token, customerId);
      dispatch(removeUploadedDocument(documentId));

      if (deletedFilename) {
        await logAnalyticUpload(customerId, token, {
          action: 'delete',
          file_name: deletedFilename,
          file_s3_key: buildDocumentS3Key(documentId, deletedFilename),
          access_permission: formatAccessPermission(
            existingDocument?.allowedRoles,
          ),
          category: detectDocumentCategory(deletedFilename),
          status: 'completed',
        });
      }

      progressToastId = toast.info('Deleting is in progress...', {
        autoClose: false,
        closeOnClick: false,
      });

      await new Promise((resolve) => {
        window.setTimeout(resolve, DELETE_SETTLE_MS);
      });

      await dispatch(fetchAllDocuments(token, customerId, true));

      if (progressToastId !== undefined) {
        toast.dismiss(progressToastId);
      }

      const uploadedDocuments =
        RootStore.getState().documents.uploadedDocuments;
      const duplicateRemaining =
        deletedFilename &&
        findUploadedDocumentByFilename(uploadedDocuments, deletedFilename);

      if (duplicateRemaining) {
        toast.warn(
          `Another copy of "${deletedFilename}" is still registered. Delete it from the list above before uploading again.`,
        );
        return;
      }

      const stillPresent = uploadedDocuments.some(
        (document: UploadedDocument) => document.documentId === documentId,
      );
      if (stillPresent) {
        toast.warn(
          'Delete is still processing on the server. Wait a moment before re-uploading the same file.',
        );
        return;
      }

      toast.success('Document deleted.');
    } catch (error) {
      if (progressToastId !== undefined) {
        toast.dismiss(progressToastId);
      }
      documentDebugError(`Failed to delete document ${documentId}`, error);
      const message =
        error instanceof Error ? error.message : 'Failed to delete document.';
      toast.error(message);
    }
  };

export const deleteFailedDocument = deleteDocument;

export const markDocumentFailed =
  (documentId: string, filename: string) => (dispatch: DocumentsDispatch) => {
    documentDebugLog(
      LOG_PREFIX,
      'Document processing failed — awaiting manual delete',
      {
        documentId,
        filename,
      },
    );
    dispatch(updateUploadedDocumentStatus(documentId, 'failed'));
    notifyDocumentFailed(filename);
  };

export const markDocumentCompleted =
  (documentId: string, filename: string) => (dispatch: DocumentsDispatch) => {
    documentDebugLog(LOG_PREFIX, 'Document completed', {
      documentId,
      filename,
    });
    dispatch(updateUploadedDocumentStatus(documentId, 'completed'));
    notifyDocumentCompleted(filename);
  };

export const addProcessingDocuments = addUploadedDocuments;

export const updateProcessingDocumentStatus = updateUploadedDocumentStatus;

export const removeProcessingDocument = removeUploadedDocument;
